import { useQuery, useQueryClient, useMutation, useInfiniteQuery } from '@tanstack/react-query';
import api from './api';

/**
 * Canonical query hooks - one per resource, replacing the parallel fetch
 * implementations that grew per page (three copies of GET /api/scans, five of
 * the clients list, three of a single scan *inside one hook*).
 *
 * Key discipline: every key is a flat array starting with the resource name,
 * so invalidation can target exactly one resource
 * (`invalidateQueries({ queryKey: ['clients'] })`).
 *
 * staleTime defaults are deliberate: reference data (clients, accounts)
 * changes rarely and reads instantly from cache on navigation; live data
 * (scans in flight) is invalidated by WebSocket events rather than refetched
 * on a timer.
 */

export const queryKeys = {
	clients: ['clients'],
	client: (id) => ['clients', id],
	accounts: ['accounts'],
	account: (id) => ['accounts', id],
	accountsByClient: (clientId) => ['accounts', 'client', clientId],
	scans: (filters) => (filters ? ['scans', filters] : ['scans']),
	scan: (id) => ['scans', id],
	scanPosts: (id, params) => ['scans', id, 'posts', params || null],
	dashboard: (timeframe) => ['dashboard', timeframe || 'default'],
	schedules: ['schedules'],
	reports: ['reports'],
	heartbeatIndex: (params) => ['heartbeat', 'index', params || null],
	heartbeatRuns: (accountId) => ['heartbeat', accountId, 'runs'],
	heartbeatRun: (runId, revision) => revision ? ['heartbeat', 'run', runId, { revision: String(revision) }] : ['heartbeat', 'run', runId],
	heartbeatTimeline: (accountId, range) => ['heartbeat', accountId, 'timeline', range],
	heartbeatGrid: (accountId, params) => ['heartbeat', accountId, 'grid', params || null],
	heartbeatReplies: (accountId, params) => ['heartbeat', accountId, 'replies', params || null],
};

const MINUTE = 60 * 1000;

export function useClients(options = {}) {
	return useQuery({
		queryKey: queryKeys.clients,
		queryFn: async () => {
			// limit=200 (the server cap): this hook feeds pickers that must show
			// every client. Without it the server defaults to 20 and a larger
			// workspace silently loses clients from every dropdown.
			const body = await api.get('/api/clients', { limit: 200 });
			return body?.data?.clients || body?.data || [];
		},
		staleTime: 2 * MINUTE,
		...options,
	});
}

export function useAccountsByClient(clientId, options = {}) {
	return useQuery({
		queryKey: queryKeys.accountsByClient(clientId),
		queryFn: async () => {
			const body = await api.get(`/api/accounts/client/${clientId}`);
			return body?.data?.accounts || [];
		},
		enabled: !!clientId,
		staleTime: 2 * MINUTE,
		...options,
	});
}

/**
 * One account, with its client populated.
 *
 * Needed because a page reached by deep link knows only an accountId, and
 * `useAccountsByClient` cannot help until you already know the client. Every
 * screen that identifies an account by handle depends on this.
 */
export function useAccount(accountId, options = {}) {
	return useQuery({
		queryKey: queryKeys.account(accountId),
		queryFn: async () => {
			const body = await api.get(`/api/accounts/${accountId}`);
			return body?.data?.account || body?.data || null;
		},
		enabled: !!accountId,
		staleTime: 2 * MINUTE,
		...options,
	});
}

export function useScan(scanId, options = {}) {
	return useQuery({
		queryKey: queryKeys.scan(scanId),
		queryFn: async () => {
			const body = await api.get(`/api/scans/${scanId}`);
			return body?.data || body;
		},
		enabled: !!scanId,
		...options,
	});
}

export function useSchedules(options = {}) {
	return useQuery({
		queryKey: queryKeys.schedules,
		queryFn: async () => {
			const body = await api.get('/api/scheduled-scans');
			return body?.data || [];
		},
		staleTime: MINUTE,
		...options,
	});
}

export function useDashboardData(timeframe = '30d', options = {}) {
	return useQuery({
		queryKey: queryKeys.dashboard(timeframe),
		queryFn: async () => {
			const body = await api.get(`/api/dashboard?timeframe=${timeframe}`);
			return body?.data || {};
		},
		staleTime: 2 * MINUTE,
		...options,
	});
}

/**
 * Every pulse across every account - the Heartbeat index.
 *
 * Polls while anything in the page is still running, so several pulses started
 * from here can be watched in one place. `refetchInterval` takes the function
 * form so it reads status off the query itself rather than a value computed
 * after the call.
 */
export function useHeartbeatIndex(params, options = {}) {
	return useQuery({
		queryKey: queryKeys.heartbeatIndex(params),
		queryFn: async () => {
			const body = await api.get('/api/heartbeat/runs', params);
			return { runs: body?.data?.runs || [], nextCursor: body?.data?.nextCursor || null, truncated: !!body?.data?.truncated };
		},
		staleTime: 15 * 1000,
		refetchInterval: (q) => {
			const runs = q.state.data?.runs || [];
			return runs.some((r) => ['queued', 'collecting', 'labeling'].includes(r.status)) ? 10000 : false;
		},
		refetchIntervalInBackground: false,
		...options,
	});
}

export function useHeartbeatRuns(accountId, options = {}) {
	return useQuery({
		queryKey: queryKeys.heartbeatRuns(accountId),
		queryFn: async () => (await api.get(`/api/heartbeat/accounts/${accountId}/runs`, { limit: 20 }))?.data?.runs || [],
		enabled: !!accountId,
		staleTime: MINUTE,
		...options,
	});
}

export function useHeartbeatRun(runId, options = {}) {
	const { revision, ...queryOptions } = options;
	return useQuery({
		queryKey: queryKeys.heartbeatRun(runId, revision),
		queryFn: async () => {
			const data = (revision ? await api.get(`/api/heartbeat/runs/${runId}`, { revision }) : await api.get(`/api/heartbeat/runs/${runId}`))?.data;
			return data ? { ...data, _id: data._id || data.runId } : null;
		},
		enabled: !!runId,
		staleTime: 0,
		...queryOptions,
	});
}

export function useHeartbeatTimeline(accountId, range, options = {}) {
	return useQuery({
		queryKey: queryKeys.heartbeatTimeline(accountId, range),
		queryFn: async () => (await api.get(`/api/heartbeat/accounts/${accountId}/timeline`, range))?.data || { bucket: 'hour', points: [] },
		enabled: !!accountId,
		staleTime: 2 * MINUTE,
		...options,
	});
}

export function useHeartbeatGrid(accountId, params, options = {}) {
	return useQuery({
		queryKey: queryKeys.heartbeatGrid(accountId, params),
		queryFn: async () => (await api.get(`/api/heartbeat/accounts/${accountId}/grid`, params))?.data || { rows: [], tiles: {} },
		enabled: !!accountId,
		staleTime: 2 * MINUTE,
		...options,
	});
}

export function useHeartbeatReplies(accountId, params, options = {}) {
	return useInfiniteQuery({
		queryKey: queryKeys.heartbeatReplies(accountId, params),
		queryFn: async ({ pageParam }) =>
			(await api.get(`/api/heartbeat/accounts/${accountId}/replies`, { ...params, limit: 25, cursor: pageParam || undefined }))?.data || { replies: [], nextCursor: null },
		initialPageParam: null,
		getNextPageParam: (last) => last?.nextCursor || undefined,
		enabled: !!accountId && options.enabled !== false,
		staleTime: 5 * MINUTE,
	});
}

export function useTakePulse(accountId) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ requestedPosts }) => (await api.post('/api/heartbeat/runs', { accountId, requestedPosts }))?.data,
		// Awaited: mutateAsync (and isPending) must not resolve/settle until the
		// runs list has actually refetched. A future edit to this callback that
		// adds a statement before invalidateQueries without returning/awaiting
		// it would silently reintroduce the double-submit race (empty state
		// flashes back, a second click starts a second run) - spelling out the
		// async/await here makes that mistake harder to make by accident.
		onSuccess: async () => { await qc.invalidateQueries({ queryKey: queryKeys.heartbeatRuns(accountId) }); },
	});
}

/**
 * Take a pulse on any account - the index starts runs for accounts it is not
 * scoped to, so the id comes with the call rather than with the hook.
 * Invalidates the whole heartbeat tree, since a new run changes both the index
 * and that account's own history.
 */
export function useTakePulseFor() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ accountId, requestedPosts }) => (await api.post('/api/heartbeat/runs', { accountId, requestedPosts }))?.data,
		onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['heartbeat'] }); },
	});
}

export function useRetryLabeling(accountId) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (runId) => (await api.post(`/api/heartbeat/runs/${runId}/retry-labeling`))?.data,
		onSuccess: async (data, runId) => {
			if (data?.review) qc.setQueryData(queryKeys.heartbeatRun(runId), (previous) => previous ? { ...previous, contextReview: data.review } : previous);
			await Promise.all([
				qc.invalidateQueries({ queryKey: ['heartbeat', accountId] }),
				qc.invalidateQueries({ queryKey: queryKeys.heartbeatRun(runId) }),
				qc.invalidateQueries({ queryKey: ['heartbeat', 'index'] }),
			]);
		},
	});
}

/** Invalidation helpers so components never hand-roll key arrays. */
export function useInvalidate() {
	const qc = useQueryClient();
	return {
		clients: () => qc.invalidateQueries({ queryKey: queryKeys.clients }),
		accounts: () => qc.invalidateQueries({ queryKey: queryKeys.accounts }),
		scans: () => qc.invalidateQueries({ queryKey: ['scans'] }),
		schedules: () => qc.invalidateQueries({ queryKey: queryKeys.schedules }),
		dashboard: () => qc.invalidateQueries({ queryKey: ['dashboard'] }),
		heartbeat: (accountId) => qc.invalidateQueries({ queryKey: accountId ? ['heartbeat', accountId] : ['heartbeat'] }),
	};
}
